/**
 * Chart-of-accounts template WRITE endpoints — request schemas (Pattern B).
 *
 * Served by `apps/api/src/routes/accounting/coa-templates.ts` under
 * `/api/v1/coa-templates`: super-admin-only CRUD of the GLOBAL templates
 * (`organization_id IS NULL`) and their account rows. Writes to
 * `chart_of_accounts_templates` / `chart_of_accounts_template_accounts` are
 * backend-only once
 * `20261001070733_accounting_settings_backend_write_lockdown.sql` is pushed.
 *
 * `.strict()`: `organization_id`, `created_by`, `template_id` (the URL),
 * `account_level` (derived by `coa_template_account_derive_hierarchy`) and
 * `account_number` (deprecated and ignored — applying a template numbers every
 * account from its subtype's house code, docs/CHART_OF_ACCOUNTS.md) are never
 * accepted from the body.
 */
import { z } from "zod";
const templateFields = {
    name: z.string().trim().min(1).max(255),
    description: z.string().nullable().optional(),
    sort_order: z.number().int().optional(),
    is_default: z.boolean().optional(),
    is_active: z.boolean().optional(),
};
/** POST /templates — a new GLOBAL template. */
export const createCoaTemplateSchema = z.object(templateFields).strict();
/** PATCH /templates/:templateId — any subset, at least one field. */
export const updateCoaTemplateSchema = z
    .object(templateFields)
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
});
const accountFields = {
    parent_id: z.string().uuid().nullable().optional(),
    account_name: z.string().trim().min(1).max(255),
    account_description: z.string().nullable().optional(),
    // Optional on a SUB-account: the BEFORE trigger copies the parent's
    // classification over whatever is sent — except a CONTRA subtype of the
    // parent's type, which a sub-row keeps (with its side; 20261006220000). A
    // top-level row needs both (the route refuses a top-level create without
    // them).
    category_id: z.string().uuid().optional(),
    type_id: z.string().uuid().optional(),
    subtype_id: z.string().uuid().nullable().optional(),
    normal_balance: z.enum(["debit", "credit"]),
    requires_project: z.boolean().optional(),
    sort_order: z.number().int().optional(),
};
/** POST /templates/:templateId/accounts. */
export const createCoaTemplateAccountSchema = z.object(accountFields).strict();
/** PATCH /templates/:templateId/accounts/:accountId — any subset, at least one field. */
export const updateCoaTemplateAccountSchema = z
    .object(accountFields)
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
});
//# sourceMappingURL=coaTemplates.js.map